import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listDropdownFields } from '@modules/playbook/services/api';
import type { DynamicFieldConfig } from '@modules/playbook/types';
import type { ApiResponse } from '@shared/interface';
import { IDropdownOptions } from '@shared/interface';

interface OfferConfigContextValue {
  isLoading: boolean;
  error: Error | null;
  premiumOptions: IDropdownOptions[];
  countryOptions: IDropdownOptions[];
  scopeOptions: IDropdownOptions[];
  currencyOptions: IDropdownOptions[];
  categoryOptions: IDropdownOptions[];
  priceOptions: IDropdownOptions[];
}

const OfferConfigContext = createContext<OfferConfigContextValue | undefined>(undefined);

interface OfferConfigProviderProps {
  children: ReactNode;
}

// Helper to convert DynamicFieldConfig options to IDropdownOptions
function convertToDropdownOptions(field?: DynamicFieldConfig): IDropdownOptions[] {
  if (!field?.options) return [];
  return field.options.map((opt) => ({
    optionId: opt.optionId,
    label: opt.label,
    value: opt.value,
  }));
}

export const OfferConfigProvider: React.FC<OfferConfigProviderProps> = ({ children }) => {
  const {
    data: dropdownFields,
    isLoading,
    error,
  } = useQuery<ApiResponse<DynamicFieldConfig[]>, Error, DynamicFieldConfig[]>({
    queryKey: ['offerDropdownFields', 'Offer'],
    queryFn: () => listDropdownFields('Offer'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    select: (data) => data.data,
  });

  // Extract options from dropdown fields by key
  const premiumOptions = useMemo(() => {
    const field = dropdownFields?.find((f) => f.key === 'premium_ids');
    return convertToDropdownOptions(field);
  }, [dropdownFields]);

  const countryOptions = useMemo(() => {
    const field = dropdownFields?.find((f) => f.key === 'country');
    return convertToDropdownOptions(field);
  }, [dropdownFields]);

  const scopeOptions = useMemo(() => {
    const field = dropdownFields?.find((f) => f.key === 'scope');
    return convertToDropdownOptions(field);
  }, [dropdownFields]);

  const currencyOptions = useMemo(() => {
    const field = dropdownFields?.find((f) => f.key === 'currency');
    return convertToDropdownOptions(field);
  }, [dropdownFields]);

  const categoryOptions = useMemo(() => {
    const field = dropdownFields?.find((f) => f.key === 'offer_category');
    return convertToDropdownOptions(field);
  }, [dropdownFields]);

  const priceOptions = useMemo(() => {
    const field = dropdownFields?.find((f) => f.key === 'prices');
    return convertToDropdownOptions(field);
  }, [dropdownFields]);

  const value = useMemo<OfferConfigContextValue>(
    () => ({
      isLoading,
      error,
      premiumOptions,
      countryOptions,
      scopeOptions,
      currencyOptions,
      categoryOptions,
      priceOptions,
    }),
    [isLoading, error, premiumOptions, countryOptions, scopeOptions, currencyOptions, categoryOptions, priceOptions],
  );

  return <OfferConfigContext.Provider value={value}>{children}</OfferConfigContext.Provider>;
};

export const useOfferConfig = (): OfferConfigContextValue => {
  const context = useContext(OfferConfigContext);
  if (!context) throw new Error('useOfferConfig must be used within an OfferConfigProvider');
  return context;
};
